import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Form,
  message,
  Modal,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Tour,
} from "antd";
import {
  AppstoreOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  TableOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PvTable from "../../components/Table/PvTable";
import Popup from "../../components/Popup";
import usePrismPaymentService from "../../services/Prism/paymentService";
import usePrismSubscriptionService from "../../services/Prism/subscriptionService";
import {
  clearPrismSubscriptionFocus,
  setPrismReportsTab,
  setPrismTab,
} from "../../state/slices/generalSlice";
import PrismPaymentForm from "./PrismPaymentForm";
import PrismSubscriptionCardView from "./PrismSubscriptionCardView";
import PrismSubscriptionForm from "./PrismSubscriptionForm";
import PrismSubscriptionDetailsModal from "./PrismSubscriptionDetailsModal";
import PrismTruncatedText from "./PrismTruncatedText";
import "../../styles/pages/prism/prismSubscriptions.scss";
import {
  buildOfficeFilterOptions,
  buildPrismCreateUnitOptions,
  buildPrismManageOfficeOptions,
  buildPrismManageUnitOptions,
  canManageAnyPrismOffice,
  canCreatePrismScope,
  canManagePrismScope,
  canManagePrismRecord,
  CRITICALITY_COLORS,
  CRITICALITY_LABELS,
  CRITICALITY_PRIORITY_SEQUENCE,
  DEFAULT_CRITICALITY_PRIORITY,
  formatDate,
  formatLabel,
  formatOfficeName,
  formatSubOfficeName,
  getCriticality,
  getDateTime,
  getEffectiveSubscriptionStatus,
  getOfficeFilterValue,
  getPrismPriorityRank,
  getSinglePrismAdminScope,
  groupPrismSubscriptionsByOffice,
  isFullPrismRecord,
  LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS,
  renderRestrictedValue,
  STATUS_COLORS,
  STATUS_FILTER_OPTIONS,
} from "./prismSubscriptionUtils";

const CRITICALITY_PRIORITY_LABELS = {
  urgent: "Urgent first",
  due_soon: "Due Soon first",
  overdue: "Overdue first",
  safe: "Safe first",
};

const CRITICALITY_FILTER_OPTIONS = Object.entries(CRITICALITY_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

const renderCriticality = (criticality) => {
  if (!criticality) return "-";

  return (
    <Tag
      color={CRITICALITY_COLORS[criticality] || "default"}
      style={
        criticality === "overdue"
          ? { color: "#fff", borderColor: "#000" }
          : undefined
      }
    >
      {CRITICALITY_LABELS[criticality] || formatLabel(criticality)}
    </Tag>
  );
};

const renderStatus = (status) => {
  if (!status) return "-";

  return (
    <Tag color={STATUS_COLORS[status] || "blue"}>{formatLabel(status)}</Tag>
  );
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  const firstFieldErrors = Object.values(error?.errors || {})[0];
  if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
    return firstFieldErrors[0];
  }

  if (typeof firstFieldErrors === "string" && firstFieldErrors) {
    return firstFieldErrors;
  }

  if (typeof error === "string") return error;
  if (typeof error?.message === "string") return error.message;

  return fallback;
};

const applyApiValidationErrors = (form, error) => {
  const fieldErrors = Object.entries(error?.errors || {})
    .map(([name, errors]) => ({
      name,
      errors: (Array.isArray(errors) ? errors : [errors])
        .filter(Boolean)
        .map(String),
    }))
    .filter(({ errors }) => errors.length > 0);

  if (fieldErrors.length > 0) {
    form.setFields(fieldErrors);
  }
};

const toFormDate = (value) => {
  if (!value) return null;

  const dateValue =
    typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)
      ? value.slice(0, 10)
      : value;
  const parsed = dayjs(dateValue);

  return parsed.isValid() ? parsed : null;
};

const getSubscriptionFormValues = (subscription = {}) => ({
  subscription_name: subscription.subscription_name || "",
  subscription_details: subscription.subscription_details || null,
  subscriber_email: subscription.subscriber_email || null,
  subscriber_additional_emails:
    subscription.subscriber_additional_emails || [],
  subscriber_department: subscription.subscriber_department,
  subscriber_sub_department: subscription.subscriber_sub_department || null,
  subscription_type: subscription.subscription_type || "premium",
  contract_status: subscription.contract_status || "active",
  contract_renewal_frequency:
    subscription.contract_renewal_frequency || "annual",
  auto_renewal_status: Boolean(subscription.auto_renewal_status),
  contract_start_date: toFormDate(subscription.contract_start_date),
  contract_end_date: toFormDate(subscription.contract_end_date),
  contract_renewal_date: toFormDate(subscription.contract_renewal_date),
  billing_cycle: subscription.billing_cycle || "annual",
  payment_method: subscription.payment_method || "other",
  bank_name: subscription.bank_name || null,
  account_holder_name: subscription.account_holder_name || "",
});

const PrismSubscriptions = () => {
  const dispatch = useDispatch();
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [viewMode, setViewMode] = useState("card");
  const [criticalityPriority, setCriticalityPriority] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [detailsSubscription, setDetailsSubscription] = useState(null);
  const [focusedSubscriptionId, setFocusedSubscriptionId] = useState(null);
  const [activationSubscriptionId, setActivationSubscriptionId] = useState(null);
  const [paymentSubscription, setPaymentSubscription] = useState(null);
  const [officeFilter, setOfficeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [criticalityFilterValue, setCriticalityFilterValue] = useState(null);
  const [createSubmitError, setCreateSubmitError] = useState(null);
  const [editSubmitError, setEditSubmitError] = useState(null);
  const [deleteSubmitError, setDeleteSubmitError] = useState(null);
  const [paymentSubmitError, setPaymentSubmitError] = useState(null);
  const [activationSubmitError, setActivationSubmitError] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const addSubscriptionButtonRef = useRef(null);
  const officeFilterRef = useRef(null);
  const statusFiltersRef = useRef(null);
  const viewSwitchRef = useRef(null);
  const subscriptionsListRef = useRef(null);
  const managePaymentsButtonRef = useRef(null);
  const { details = {} } = useSelector((state) => state.accountDetailSlice);
  const requestedSubscriptionFocusId = useSelector(
    (state) => state.generalSlice.prismSubscriptionFocusId,
  );
  const {
    getSubscriptions,
    subscriptionLoading,
    subscriptionResponse,
    subscriptionError,
    createSubscription,
    createLoading,
    updateSubscription,
    updateLoading,
    updateSubscriptionActivation,
    activationLoading,
    deleteSubscription,
    deleteLoading,
  } = usePrismSubscriptionService();
  const { createPayment, createPaymentLoading } = usePrismPaymentService();

  useEffect(() => {
    getSubscriptions().catch(() => {
      // useAxios stores the load error for the inline alert.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canManagePrism = useMemo(() => {
    return canManageAnyPrismOffice(details);
  }, [details]);

  const tourSeenStorageKey = details?.email
    ? `prism-subscriptions-tour-seen:${details.email}`
    : null;

  useEffect(() => {
    if (!canManagePrism || !tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [canManagePrism, tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Add a subscription",
        description:
          "Create a new subscription for your office or unit here — you'll set its name, billing details, and renewal schedule.",
        target: () => addSubscriptionButtonRef.current,
      },
      {
        title: "Filter by office",
        description: "Narrow the list down to a specific office.",
        target: () => officeFilterRef.current,
      },
      {
        title: "Filter by status or urgency",
        description:
          "Use these to quickly spot subscriptions that need attention.",
        target: () => statusFiltersRef.current,
      },
      {
        title: "Table or card view",
        description: "Switch between a table or card layout, whichever you prefer.",
        target: () => viewSwitchRef.current,
      },
      {
        title: "Your subscriptions",
        description:
          "Click any subscription to view its full details, or use the inline actions to edit it, add a payment, enable/disable it, or delete it.",
        target: () => subscriptionsListRef.current,
      },
      {
        title: "Payment history",
        description:
          "Head here anytime to see the full payment history across your subscriptions.",
        target: () => managePaymentsButtonRef.current,
      },
    ],
    [],
  );

  const subscriptions = useMemo(
    () =>
      Array.isArray(subscriptionResponse?.data)
        ? subscriptionResponse.data
        : [],
    [subscriptionResponse],
  );

  const officeFilterOptions = useMemo(
    () => buildOfficeFilterOptions(subscriptions),
    [subscriptions],
  );

  const tableData = useMemo(() => {
    const rowsWithAccessMetadata = subscriptions.map((item, index) => {
      const isFullRecord = isFullPrismRecord(item, details);

      return {
        originalIndex: index,
        key: item.id,
        ...item,
        criticality: isFullRecord && item.is_enabled !== false
          ? getCriticality(
              item.contract_renewal_date,
              item.subscription_type,
              item.contract_status,
            )
          : null,
        displayStatus: getEffectiveSubscriptionStatus(item),
        isFullRecord,
        normalizedOffice: getOfficeFilterValue(item.subscriber_department),
      };
    });

    const filteredSubscriptions = rowsWithAccessMetadata.filter((item) => {
      const officeMatches = officeFilter
        ? item.normalizedOffice === officeFilter
        : true;
      const statusMatches = canManagePrism && statusFilter
        ? item.isFullRecord && item.displayStatus === statusFilter
        : true;
      const criticalityMatches = canManagePrism && criticalityFilterValue
        ? item.isFullRecord && item.criticality === criticalityFilterValue
        : true;

      return officeMatches && statusMatches && criticalityMatches;
    });

    return [...filteredSubscriptions].sort((a, b) => {
      const activeCriticalityPriority =
        criticalityPriority || DEFAULT_CRITICALITY_PRIORITY;
      const getPriorityRank = (record) => {
        if (record.criticality === activeCriticalityPriority) return -1;
        return getPrismPriorityRank(record);
      };

      const aPriorityMatch = getPriorityRank(a);
      const bPriorityMatch = getPriorityRank(b);

      if (aPriorityMatch !== bPriorityMatch) {
        return aPriorityMatch - bPriorityMatch;
      }

      const dateA = getDateTime(a.contract_renewal_date);
      const dateB = getDateTime(b.contract_renewal_date);

      if (dateA !== dateB) {
        return dateA - dateB;
      }

      const nameCompare = String(a.subscription_name || "").localeCompare(
        String(b.subscription_name || ""),
      );

      if (nameCompare !== 0) {
        return nameCompare;
      }

      return a.originalIndex - b.originalIndex;
    });
  }, [
    subscriptions,
    details,
    canManagePrism,
    criticalityPriority,
    officeFilter,
    statusFilter,
    criticalityFilterValue,
  ]);

  const groupedSubscriptions = useMemo(
    () => groupPrismSubscriptionsByOffice(tableData),
    [tableData],
  );

  useEffect(() => {
    if (!requestedSubscriptionFocusId) return;

    setFocusedSubscriptionId(requestedSubscriptionFocusId);
    setViewMode("card");
    setOfficeFilter(null);
    setStatusFilter(null);
    setCriticalityFilterValue(null);
  }, [requestedSubscriptionFocusId]);

  const handleFocusedSubscriptionRevealed = useCallback(() => {
    dispatch(clearPrismSubscriptionFocus());
  }, [dispatch]);

  const handleCriticalityPriorityToggle = (event) => {
    event.stopPropagation();
    setCriticalityPriority((currentPriority) => {
      const currentIndex =
        CRITICALITY_PRIORITY_SEQUENCE.indexOf(currentPriority);
      const nextIndex =
        currentIndex === -1
          ? 1
          : (currentIndex + 1) % CRITICALITY_PRIORITY_SEQUENCE.length;

      return CRITICALITY_PRIORITY_SEQUENCE[nextIndex];
    });
  };

  const renderCriticalityColumnTitle = () => (
    <Space className="prism-subscriptions-table__criticality-heading" size={8}>
      <span>Criticality</span>
      <Button
        size="small"
        onClick={handleCriticalityPriorityToggle}
        style={{ padding: "0 8px" }}
      >
        {criticalityPriority
          ? CRITICALITY_PRIORITY_LABELS[criticalityPriority]
          : "Default"}
      </Button>
    </Space>
  );

  const baseColumns = [
    {
      title: renderCriticalityColumnTitle,
      dataIndex: "criticality",
      key: "criticality",
      width: "15%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, renderCriticality),
    },
    {
      title: "Subscription Name",
      dataIndex: "subscription_name",
      key: "subscription_name",
      sortable: true,
      width: "15%",
      ellipsis: { showTitle: false },
      render: (value) => <PrismTruncatedText value={value} />,
    },
    {
      title: "Description",
      dataIndex: "subscription_details",
      key: "subscription_details",
      width: "15%",
      ellipsis: { showTitle: false },
      render: (value) => <PrismTruncatedText value={value} lines={2} />,
    },
    {
      title: "Availability",
      dataIndex: "is_enabled",
      key: "is_enabled",
      width: "8%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, (availability) => (
          <Tag color={availability === false ? "default" : "green"}>
            {availability === false ? "Disabled" : "Enabled"}
          </Tag>
        )),
    },
    {
      title: "Status",
      dataIndex: "contract_status",
      key: "contract_status",
      width: "8%",
      render: (value, record) =>
        renderRestrictedValue(
          record,
          getEffectiveSubscriptionStatus(record),
          details,
          renderStatus,
        ),
    },
    {
      title: "Renewal Date",
      dataIndex: "contract_renewal_date",
      key: "contract_renewal_date",
      sortable: true,
      width: "9%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, formatDate),
    },
    {
      title: "Renewal Frequency",
      dataIndex: "contract_renewal_frequency",
      key: "contract_renewal_frequency",
      width: "9%",
      render: (value, record) =>
        renderRestrictedValue(record, value, details, formatLabel),
    },
    {
      title: "Office",
      dataIndex: "subscriber_department",
      key: "subscriber_department",
      sortable: true,
      width: "11%",
      ellipsis: { showTitle: false },
      render: (value) => (
        <PrismTruncatedText value={value} formatter={formatOfficeName} />
      ),
    },
    {
      title: "Unit",
      dataIndex: "subscriber_sub_department",
      key: "subscriber_sub_department",
      width: "11%",
      ellipsis: { showTitle: false },
      render: (value) => (
        <PrismTruncatedText value={value} formatter={formatSubOfficeName} />
      ),
    },
  ];

  const visibleBaseColumns = canManagePrism
    ? baseColumns
    : baseColumns.filter((column) =>
        LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS.has(column.key),
      );

  const columns = canManagePrism
    ? [
        ...visibleBaseColumns,
        {
          title: "Actions",
          key: "actions",
          width: "22%",
          render: (_, record) => {
            if (!canManagePrismRecord(details, record)) {
              return null;
            }

            return (
              <Space
                className="prism-subscriptions-table__actions"
                size={[4, 4]}
                wrap
              >
                <Tooltip
                  title={
                    record.is_enabled !== false
                      ? "Disable this subscription"
                      : "Enable this subscription"
                  }
                >
                  <Switch
                    aria-label={`${
                      record.is_enabled !== false ? "Disable" : "Enable"
                    } ${record.subscription_name || "subscription"}`}
                    checked={record.is_enabled !== false}
                    loading={
                      activationLoading && activationSubscriptionId === record.id
                    }
                    onClick={(checked, event) => {
                      event.stopPropagation();
                      handleToggleSubscription(record, checked);
                    }}
                    size="small"
                  />
                </Tooltip>
                <Button
                  size="small"
                  icon={<DollarOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAddPaymentOpen(record);
                  }}
                >
                  Add Payment
                </Button>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEditOpen(record);
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deleteLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteSubscription(record);
                  }}
                >
                  Delete
                </Button>
              </Space>
            );
          },
        },
      ]
    : visibleBaseColumns;

  const errorMessage = getErrorMessage(
    subscriptionError,
    "Failed to load PRISM subscriptions.",
  );

  const addOfficeOptions = useMemo(
    () => buildPrismManageOfficeOptions(details),
    [details],
  );
  const fixedAddScope = useMemo(
    () => getSinglePrismAdminScope(details),
    [details],
  );
  const editOfficeOptions = useMemo(
    () =>
      buildPrismManageOfficeOptions(
        details,
        selectedSubscription?.subscriber_department,
      ),
    [details, selectedSubscription],
  );

  const handleAddSubscription = async (values, options = {}) => {
    try {
      setCreateSubmitError(null);
      const response = await createSubscription(values);
      const createdSubscription = response?.data;
      message.success("Subscription added successfully.");
      setIsAddModalOpen(false);
      addForm.resetFields();
      getSubscriptions().catch(() => {
        // The subscription was created; the inline alert will show reload issues.
      });

      if (options.addPaymentAfterCreate) {
        if (createdSubscription?.id) {
          handleAddPaymentOpen(createdSubscription);
        } else {
          message.warning(
            "Subscription added, but the payment modal could not be opened automatically.",
          );
        }
      }
    } catch (error) {
      const submitError = error?.response?.data || error?.message;
      applyApiValidationErrors(addForm, submitError);
      setCreateSubmitError(submitError);
      message.error(
        getErrorMessage(submitError, "Failed to add subscription."),
      );
    }
  };

  const handleEditOpen = (record) => {
    if (!canManagePrismRecord(details, record)) {
      message.error("You are not allowed to edit subscriptions for this office/unit.");
      return;
    }

    setSelectedSubscription(record);
    setEditSubmitError(null);
    editForm.resetFields();
    editForm.setFieldsValue(getSubscriptionFormValues(record));
    setIsEditModalOpen(true);
  };

  const handleEditSubscription = async (values) => {
    if (!selectedSubscription?.id) {
      message.error("No subscription selected.");
      return;
    }

    try {
      setEditSubmitError(null);
      await updateSubscription(selectedSubscription.id, values);
      message.success("Subscription updated successfully.");
      setIsEditModalOpen(false);
      setSelectedSubscription(null);
      editForm.resetFields();
      getSubscriptions();
    } catch (error) {
      const submitError = error?.response?.data || error?.message;
      applyApiValidationErrors(editForm, submitError);
      setEditSubmitError(submitError);
      message.error(
        getErrorMessage(submitError, "Failed to update subscription."),
      );
    }
  };

  const handleAddPaymentOpen = (record) => {
    if (!canManagePrismRecord(details, record)) {
      message.error("You are not allowed to add payments for this subscription.");
      return;
    }

    setPaymentSubscription(record);
    setPaymentSubmitError(null);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({ subscription_id: record.id });
    setIsAddPaymentModalOpen(true);
  };

  const handleAddPayment = async (values) => {
    if (!paymentSubscription?.id) {
      message.error("No subscription selected.");
      return;
    }

    try {
      setPaymentSubmitError(null);
      await createPayment({
        ...values,
        subscription_id: paymentSubscription.id,
      });
      message.success("Payment added successfully.");
      setIsAddPaymentModalOpen(false);
      setPaymentSubscription(null);
      paymentForm.resetFields();
    } catch (error) {
      const submitError = error?.response?.data || error?.message;
      applyApiValidationErrors(paymentForm, submitError);
      setPaymentSubmitError(submitError);
      message.error(getErrorMessage(submitError, "Failed to add payment."));
    }
  };

  const handleToggleSubscription = async (record, enabled) => {
    if (!canManagePrismRecord(details, record)) {
      message.error("You are not allowed to manage this subscription.");
      return;
    }

    try {
      setActivationSubmitError(null);
      setActivationSubscriptionId(record.id);
      await updateSubscriptionActivation(record.id, enabled);
      message.success(
        enabled
          ? "Subscription enabled successfully."
          : "Subscription disabled successfully.",
      );
      await getSubscriptions();
    } catch (error) {
      const submitError = error?.response?.data || error?.message;
      const enabledError = error?.response?.data?.errors?.enabled?.[0];
      setActivationSubmitError(enabledError || submitError);
      message.error(
        enabledError ||
          getErrorMessage(
            submitError,
            "Failed to update subscription availability.",
          ),
      );
    } finally {
      setActivationSubscriptionId(null);
    }
  };

  const handleDeleteSubscription = (record) => {
    if (!canManagePrismRecord(details, record)) {
      message.error("You are not allowed to delete subscriptions for this office/unit.");
      return;
    }

    setDeleteSubmitError(null);
    Modal.confirm({
      title: "Delete Subscription",
      content: `Are you sure you want to delete "${record.subscription_name}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteSubscription(record.id);
          message.success("Subscription deleted successfully.");
          getSubscriptions();
        } catch (error) {
          const submitError = error?.response?.data || error?.message;
          setDeleteSubmitError(submitError);
          message.error(
            getErrorMessage(submitError, "Failed to delete subscription."),
          );
        }
      },
    });
  };

  const handleAddCancel = () => {
    setIsAddModalOpen(false);
    setCreateSubmitError(null);
    addForm.resetFields();
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    setSelectedSubscription(null);
    setEditSubmitError(null);
    editForm.resetFields();
  };

  const handleAddPaymentCancel = () => {
    setIsAddPaymentModalOpen(false);
    setPaymentSubscription(null);
    setPaymentSubmitError(null);
    paymentForm.resetFields();
  };

  return (
    <>
      {subscriptionError && (
        <Alert
          type="error"
          showIcon
          message="Unable to load subscriptions"
          description={
            typeof errorMessage === "string"
              ? errorMessage
              : "Please try again later."
          }
          style={{ marginBottom: 12 }}
        />
      )}

      {deleteSubmitError && (
        <Alert
          type="error"
          showIcon
          message="Unable to delete subscription"
          description={getErrorMessage(
            deleteSubmitError,
            "Failed to delete subscription.",
          )}
          closable
          onClose={() => setDeleteSubmitError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {activationSubmitError && (
        <Alert
          type="error"
          showIcon
          message="Unable to update subscription availability"
          description={getErrorMessage(
            activationSubmitError,
            "Failed to update subscription availability.",
          )}
          closable
          onClose={() => setActivationSubmitError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <Card className="prism-subscriptions-panel" size="small">
        <div className="prism-subscriptions-toolbar">
          {canManagePrism && (
            <div className="prism-subscriptions-toolbar__actions">
              <Button
                ref={addSubscriptionButtonRef}
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  addForm.resetFields();
                  if (fixedAddScope) {
                    addForm.setFieldsValue({
                      subscriber_department: fixedAddScope.office,
                      subscriber_sub_department: fixedAddScope.unit,
                    });
                  }
                  addForm.setFieldValue("subscriber_email", details.email || null);
                  setCreateSubmitError(null);
                  setIsAddModalOpen(true);
                }}
              >
                Add Subscription
              </Button>
              <Button
                ref={managePaymentsButtonRef}
                icon={<DollarOutlined />}
                onClick={() => {
                  dispatch(setPrismReportsTab("payment-history"));
                  dispatch(setPrismTab("reports"));
                }}
              >
                Manage Payments
              </Button>
              <Tooltip title="Take a tour of this page">
                <Button
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setIsTourOpen(true)}
                />
              </Tooltip>
            </div>
          )}

          <div className="prism-subscriptions-toolbar__controls">
            <div ref={officeFilterRef}>
              <Select
                allowClear
                className="prism-subscriptions-toolbar__office-filter"
                showSearch
                placeholder="All Offices"
                value={officeFilter}
                options={officeFilterOptions}
                onChange={(value) => setOfficeFilter(value || null)}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
            {canManagePrism && (
              <div
                ref={statusFiltersRef}
                style={{ display: "flex", gap: 8 }}
              >
                <Select
                  allowClear
                  className="prism-subscriptions-toolbar__status-filter"
                  placeholder="All Statuses"
                  value={statusFilter}
                  options={STATUS_FILTER_OPTIONS}
                  onChange={(value) => setStatusFilter(value || null)}
                />
                <Select
                  allowClear
                  className="prism-subscriptions-toolbar__criticality-filter"
                  placeholder="All Criticality"
                  value={criticalityFilterValue}
                  options={CRITICALITY_FILTER_OPTIONS}
                  onChange={(value) => setCriticalityFilterValue(value || null)}
                />
              </div>
            )}
            <div className="prism-subscriptions-view-switch" ref={viewSwitchRef}>
              <Segmented
                aria-label="Subscription display mode"
                onChange={setViewMode}
                options={[
                  {
                    label: "Table",
                    value: "table",
                    icon: <TableOutlined />,
                  },
                  {
                    label: "Card",
                    value: "card",
                    icon: <AppstoreOutlined />,
                  },
                ]}
                value={viewMode}
              />
            </div>
          </div>
        </div>

        <div ref={subscriptionsListRef}>
          {viewMode === "table" ? (
            <PvTable
              className="prism-subscriptions-table"
              data={tableData}
              columns={columns}
              loading={subscriptionLoading}
              reverse={false}
              tableLayout="fixed"
              getRowAriaLabel={(record) =>
                `View details for ${
                  record.subscription_name || "unnamed subscription"
                }`
              }
              interactiveRows
              onRowClick={setDetailsSubscription}
            />
          ) : (
            <PrismSubscriptionCardView
              groups={groupedSubscriptions}
              loading={subscriptionLoading}
              details={details}
              deleteLoading={deleteLoading}
              activationLoadingId={
                activationLoading ? activationSubscriptionId : null
              }
              focusedSubscriptionId={focusedSubscriptionId}
              onAddPayment={handleAddPaymentOpen}
              onEdit={handleEditOpen}
              onDelete={handleDeleteSubscription}
              onToggle={handleToggleSubscription}
              onView={setDetailsSubscription}
              onFocusHandled={handleFocusedSubscriptionRevealed}
            />
          )}
        </div>
      </Card>

      <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />

      <PrismSubscriptionDetailsModal
        details={details}
        onClose={() => setDetailsSubscription(null)}
        onAddPayment={(subscription) => {
          setDetailsSubscription(null);
          handleAddPaymentOpen(subscription);
        }}
        onEdit={(subscription) => {
          setDetailsSubscription(null);
          handleEditOpen(subscription);
        }}
        open={Boolean(detailsSubscription)}
        subscription={detailsSubscription}
      />

      <Popup
        title="Add Subscription"
        isOpen={isAddModalOpen}
        footer={null}
        className="prism-subscription-popup"
        style={{ top: 10 }}
        width={1200}
        onCancel={handleAddCancel}
      >
        <div className="prism-subscription-modal-body">
          {createSubmitError && (
            <Alert
              type="error"
              showIcon
              message="Unable to add subscription"
              description={getErrorMessage(
                createSubmitError,
                "Failed to add subscription.",
              )}
              style={{ marginBottom: 12 }}
            />
          )}
          <PrismSubscriptionForm
            form={addForm}
            officeOptions={addOfficeOptions}
            loading={createLoading}
            onCancel={handleAddCancel}
            onSubmit={handleAddSubscription}
            mode="add"
            fixedOffice={fixedAddScope?.office || null}
            fixedUnit={fixedAddScope?.unit || null}
            officeLocked={Boolean(fixedAddScope)}
            unitLocked={Boolean(fixedAddScope?.unit)}
            getAllowedUnitOptions={(office, currentUnit) =>
              buildPrismCreateUnitOptions(details, office, currentUnit)
            }
            canUseOfficeLevelScope={(office) =>
              canCreatePrismScope(details, office, null)
            }
            canAddPaymentAfterCreateForScope={(office, unit) =>
              canManagePrismScope(details, office, unit)
            }
          />
        </div>
      </Popup>

      <Popup
        title="Edit Subscription"
        isOpen={isEditModalOpen}
        footer={null}
        className="prism-subscription-popup"
        style={{ top: 10 }}
        width={1200}
        onCancel={handleEditCancel}
      >
        <div className="prism-subscription-modal-body">
          {editSubmitError && (
            <Alert
              type="error"
              showIcon
              message="Unable to update subscription"
              description={getErrorMessage(
                editSubmitError,
                "Failed to update subscription.",
              )}
              style={{ marginBottom: 12 }}
            />
          )}
          <PrismSubscriptionForm
            form={editForm}
            officeOptions={editOfficeOptions}
            loading={updateLoading}
            onCancel={handleEditCancel}
            onSubmit={handleEditSubscription}
            mode="edit"
            originalSubscription={selectedSubscription}
            subscriptionDisabled={selectedSubscription?.is_enabled === false}
            getAllowedUnitOptions={(office, currentUnit) =>
              buildPrismManageUnitOptions(details, office, currentUnit)
            }
            canUseOfficeLevelScope={(office) =>
              canManagePrismScope(details, office, null)
            }
          />
        </div>
      </Popup>

      <Popup
        title={
          <PrismTruncatedText
            value={`Add Payment${
              paymentSubscription?.subscription_name
                ? ` - ${paymentSubscription.subscription_name}`
                : ""
            }`}
          />
        }
        isOpen={isAddPaymentModalOpen}
        footer={null}
        className="prism-payment-popup"
        style={{ top: 20 }}
        width={820}
        onCancel={handleAddPaymentCancel}
      >
        <div className="prism-payment-modal-body">
          {paymentSubmitError && (
            <Alert
              type="error"
              showIcon
              message="Unable to add payment"
              description={getErrorMessage(
                paymentSubmitError,
                "Failed to add payment.",
              )}
              style={{ marginBottom: 12 }}
            />
          )}
          <PrismPaymentForm
            form={paymentForm}
            loading={createPaymentLoading}
            subscriptionOptions={
              paymentSubscription
                ? [
                    {
                      value: paymentSubscription.id,
                      label: paymentSubscription.subscription_name,
                    },
                  ]
                : []
            }
            subscriptionLocked
            onCancel={handleAddPaymentCancel}
            onSubmit={handleAddPayment}
            mode="add"
          />
        </div>
      </Popup>
    </>
  );
};

export default PrismSubscriptions;
